import { RecipeFormData } from '@/types/recipe';

/**
 * Typed handoff for pre-filled recipe data passed between flows
 * (studio review page / assistant widget) and the recipe creation form.
 *
 * Replaces the previous single-consume pattern (removeItem on read) which
 * destroyed the data on refresh, leaving the user with a blank form.
 * The data now survives refresh and is only cleared on successful save.
 * A 15-minute expiry bounds staleness if the user abandons the flow.
 */

const STORAGE_KEY = 'pendingRecipeData';
const EXPIRY_MS = 15 * 60 * 1000;

interface PendingRecipePayload {
  data: Partial<RecipeFormData> & {
    autoGenerateImage?: boolean;
    fromStudio?: boolean;
    studioPersona?: string;
    fromAssistant?: boolean;
  };
  savedAt: number;
}

export function writePendingRecipe(
  data: PendingRecipePayload['data'],
): void {
  const payload: PendingRecipePayload = { data, savedAt: Date.now() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
  }
}

/**
 * Reads pending recipe data. Returns null when absent, expired, or corrupt
 * (corrupt entries are removed). Does NOT delete the entry — the caller keeps
 * it so a refresh re-populates the form; call clearPendingRecipe() after save.
 */
export function readPendingRecipe(): PendingRecipePayload['data'] | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingRecipePayload;
    if (!parsed || typeof parsed !== 'object' || !parsed.data) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.savedAt > EXPIRY_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearPendingRecipe(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
